import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { useState } from "react";
import { VideoDialog } from "./video-dialog";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";

interface VideoSectionProps {
  title: string;
  videoUrl: string | undefined;
  onUpload: (file: File) => Promise<void>;
  onDelete?: () => Promise<void>;
  isUploading?: boolean;
  maxSize?: number; // Size in MB
  disabled?: boolean;
}

export function VideoSection({
  title,
  videoUrl,
  onUpload,
  onDelete,
  maxSize = 100, // Default to 100MB
  disabled = false,
}: VideoSectionProps) {
  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { url: signedVideoUrl } = useSignedMedia(videoUrl, videoUrl);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      if (onDelete) {
        await onDelete();
      }
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <Card className="bg-white">
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="video-section" className="border-none">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex flex-col items-start gap-1">
              <span className="text-md font-medium">{title}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="space-y-4">
              <div className="flex justify-end gap-2">
                {signedVideoUrl && (
                  <Button
                    variant="destructive"
                    onClick={() => setIsDeleteDialogOpen(true)}
                    className="rounded-xl"
                    disabled={disabled}
                  >
                    Delete Video
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setIsVideoDialogOpen(true)}
                  className="rounded-xl"
                  disabled={disabled}
                >
                  {signedVideoUrl ? "Change Video" : "Upload Video"}
                </Button>
              </div>

              {signedVideoUrl && (
                <div className="bg-muted/10 overflow-hidden rounded-xl border">
                  <video
                    src={signedVideoUrl}
                    className="max-h-[300px] w-full"
                    controls
                    preload="metadata"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}

              <VideoDialog
                open={isVideoDialogOpen}
                onClose={() => setIsVideoDialogOpen(false)}
                onUpload={onUpload}
                currentVideoUrl={signedVideoUrl}
                title={title}
                maxSize={maxSize}
              />

              <ConfirmActionDialog
                isOpen={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                onConfirm={handleDelete}
                title="Do you want to delete this video?"
                description="This action cannot be undone."
                loading={isDeleting}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}
